import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Popover,
  List,
  ListItemButton,
  InputAdornment,
  Button,
  Chip,
  Menu,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, functions, storage } from '../firebase';
import { useAuth } from '../AuthContext';
import type { MessageChannel, ChannelMessage } from '../types/messaging';
import {
  getChannelConfig,
  formatBubbleTime,
  formatChatDate,
  parseContactId,
  formatPhoneDisplay,
  formatUnsupportedMessageLabel,
  renderWhatsAppFormattedText,
  getMessageDisplayText,
  getMessageTemplateButtons,
  getTemplateDisplayName,
} from '../utils/messaging';

const GOLD = '#9d6f32';
const MUTED = '#6b6b6b';
// Messages per page; the newest page loads first.
const MESSAGE_PAGE = 200;
const BORDER = '#e8e8e8';
const TEXT = '#1a1a1a';
const FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const WHATSAPP_HEADER_PROJECT_LOGO_TOKEN = '{{project_logo_url}}';
const WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN =
  '{{voucher_or_project_logo_url}}';
const WHATSAPP_HEADER_PROJECT_BROCHURE_TOKEN = '{{project_brochure_url}}';
type WhatsappAttachmentKind = 'image' | 'video' | 'document';

type PendingWhatsappAttachment = {
  file: File;
  kind: WhatsappAttachmentKind;
  previewUrl?: string;
};

const WHATSAPP_ATTACHMENT_LIMITS: Record<WhatsappAttachmentKind, number> = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

function sanitizeWhatsappAttachmentName(name: string): string {
  return String(name || 'attachment')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120) || 'attachment';
}

function detectWhatsappAttachmentKind(file: File): WhatsappAttachmentKind {
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  return 'document';
}

function interactiveMessageParts(message: ChannelMessage) {
  if (message.type !== 'interactive') return { document: null, buttons: [] as string[] };
  const raw = (message.rawMessagePayload || message.providerPayload || {}) as any;
  const interactive = raw.interactive || {};
  const headerDocument = interactive.header?.type === 'document' ? interactive.header.document : null;
  const url = String(headerDocument?.link || '').trim();
  const safeUrl = /^https:\/\//i.test(url) ? url : null;
  const document = headerDocument ? {
    filename: String(headerDocument.filename || 'Document').trim(),
    url: safeUrl,
  } : null;
  const buttons: string[] = Array.isArray(interactive.action?.buttons)
    ? interactive.action.buttons.map((button: any) => String(button?.reply?.title || '').trim()).filter(Boolean)
    : [];
  return { document, buttons };
}

function formatWhatsappPersonName(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatWhatsappVariableValue(sourceKey: string, value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (!['customer_name', 'customer_first_name', 'staff_name', 'contact_name', 'broker_name', 'agency_name', 'lead_name', 'name'].includes(String(sourceKey || '').trim())) {
    return raw;
  }
  const formatted = formatWhatsappPersonName(raw);
  if (String(sourceKey || '').trim() === 'customer_first_name') {
    return formatted.split(/\s+/).filter(Boolean)[0] || '';
  }
  return formatted;
}

type WhatsappTemplateRow = {
  id: string;
  label?: string;
  name?: string;
  language?: string;
  headerFormat?: string;
  headerImageUrl?: string;
  headerDocumentUrl?: string;
  headerLatitude?: string;
  headerLongitude?: string;
  headerLocationName?: string;
  headerLocationAddress?: string;
  bodyText?: string | null;
  variables?: Array<{
    key?: string;
    label?: string;
    defaultValue?: string;
    source?: string;
  }>;
  buttonVariables?: Array<{
    index?: string;
    key?: string;
    label?: string;
    defaultValue?: string;
    source?: string;
  }>;
};

type WhatsappQuickReplyRow = {
  id: string;
  label?: string;
  text?: string;
  category?: string;
  scope?: 'global' | 'project';
  projectId?: string | null;
  active?: boolean;
  sortOrder?: number;
};

interface ChannelChatProps {
  channel: MessageChannel;
  contactId: string;
  contactName?: string | null;
  leadId?: string | null;
  brokerId?: string | null;
  customerUnitId?: string | null;
  customerUnitNumber?: string | null;
  customerProjectName?: string | null;
  customerName?: string | null;
  customerLinkedUnits?: Array<{ projectId: string; projectName: string; unitId: string; unitNumber: string }>;
  projectId?: string | null;
  height?: string | number;
  showHeader?: boolean;
  onBack?: () => void;
  onNameUpdated?: (phone: string, name: string) => void;
  onAddLead?: (phone: string, name: string) => void;
  onOpenLead?: (leadId: string) => void;
  onOpenBroker?: (brokerId: string) => void;
  onOpenCustomerUnit?: (unitId: string) => void;
  hasLead?: boolean;
  hasBroker?: boolean;
  hasCustomer?: boolean;
  canOpenCustomerUnit?: boolean;
  onSendBrochure?: (anchorEl: HTMLElement, lastInboundAt: Date | null) => void;
}

export default function ChannelChat({
  channel,
  contactId,
  contactName,
  leadId,
  brokerId,
  customerUnitId,
  customerUnitNumber,
  customerProjectName,
  customerName,
  customerLinkedUnits,
  projectId,
  height = '100%',
  showHeader = true,
  onBack,
  onNameUpdated,
  onAddLead,
  onOpenLead,
  onOpenBroker,
  onOpenCustomerUnit,
  hasLead,
  hasBroker,
  hasCustomer,
  canOpenCustomerUnit,
  onSendBrochure,
}: ChannelChatProps) {
  const { currentUser } = useAuth();
  const config = getChannelConfig(channel);
  const { identifier: contactPhone } = parseContactId(contactId);

  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentMenuAnchorEl, setAttachmentMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [pendingAttachment, setPendingAttachment] =
    useState<PendingWhatsappAttachment | null>(null);
  const [senders, setSenders] = useState<any[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState('');
  const [templates, setTemplates] = useState<WhatsappTemplateRow[]>([]);
  const [templateEdits, setTemplateEdits] = useState<Record<string, any>>({});
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templatesAnchorEl, setTemplatesAnchorEl] = useState<HTMLElement | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [quickReplies, setQuickReplies] = useState<WhatsappQuickReplyRow[]>([]);
  const [quickRepliesAnchorEl, setQuickRepliesAnchorEl] = useState<HTMLElement | null>(null);
  const [quickReplySearch, setQuickReplySearch] = useState('');
  const [quickReplyCategoryFilter, setQuickReplyCategoryFilter] = useState('all');
  const [projectMeta, setProjectMeta] = useState<{
    name?: string | null;
    logoUrl?: string | null;
    brochureUrl?: string | null;
    websiteSlug?: string | null;
  } | null>(null);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState<Record<string, string>>({});
  const [resendingMessageId, setResendingMessageId] = useState<string | null>(null);
  const [documentVisible, setDocumentVisible] = useState(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible'
  );
  const [windowFocused, setWindowFocused] = useState(
    typeof document === 'undefined' ? true : document.hasFocus()
  );
  const [effectiveProjectId, setEffectiveProjectId] = useState<string | null>(
    projectId || null
  );

  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pageSize, setPageSize] = useState(MESSAGE_PAGE);
  const [hasEarlier, setHasEarlier] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentAcceptRef = useRef<string>('*/*');
  const customerTooltip = useMemo(() => {
    if (!hasCustomer && !customerUnitId) return null;
    return (
      <Box sx={{ p: 0.25 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
          {customerName || contactName || 'Customer'}
        </Typography>
        {(customerLinkedUnits?.length ? customerLinkedUnits : [{ projectId: '', projectName: customerProjectName || '—', unitId: customerUnitId || '', unitNumber: customerUnitNumber || '—' }]).map((unit) => (
          <Typography key={`${unit.projectId}/${unit.unitId}`} sx={{ fontSize: 11, lineHeight: 1.3 }}>
            {unit.projectName} · Unit {unit.unitNumber}
          </Typography>
        ))}
      </Box>
    );
  }, [contactName, customerName, customerProjectName, customerUnitId, customerUnitNumber, customerLinkedUnits, hasCustomer]);

  const handleSaveName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || !contactPhone) return;
    const phone10 = contactPhone.slice(-10);
    try {
      await setDoc(doc(db, 'whatsappContacts', phone10), {
        phone: contactPhone,
        confirmedName: trimmed,
        confirmedBy: currentUser?.email || null,
        confirmedAt: serverTimestamp(),
      }, { merge: true });
      onNameUpdated?.(phone10, trimmed);
    } catch (e) {
      console.error('Failed to save contact name', e);
    }
    setEditingName(false);
  };

  useEffect(() => {
    if (channel !== 'whatsapp') return;
    const unsub = onSnapshot(
      query(collection(db, 'whatsappSenders'), where('active', '==', true)),
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSenders(items);
        setSelectedSenderId((prev) => {
          if (prev) return prev;
          const def = items.find((s: any) => s.isDefault) || items[0];
          return def?.id || '';
        });
      }
    );
    return unsub;
  }, [channel]);

  useEffect(() => {
    if (channel !== 'whatsapp') return;
    setTemplatesLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'whatsappTemplates'), orderBy('updatedAt', 'desc')),
      (snap) => {
        const items = snap.docs
          .map((d) => ({
            id: d.id,
            ...(d.data() as Omit<WhatsappTemplateRow, 'id'> & {
              showInLeadChat?: boolean;
            }),
          }))
          .filter((row) => row.showInLeadChat === true);
        setTemplates(items);
        setSelectedTemplateId((prev) =>
          prev && items.some((item) => item.id === prev) ? prev : ''
        );
        setTemplatesLoading(false);
      },
      () => setTemplatesLoading(false)
    );
    return unsub;
  }, [channel]);

  useEffect(() => {
    if (channel !== 'whatsapp') return;
    return onSnapshot(collection(db, 'whatsappTemplateEdits'), (snap) => {
      const edits: Record<string, any> = {};
      snap.docs.forEach((row) => { edits[row.id] = row.data(); });
      setTemplateEdits(edits);
    });
  }, [channel]);

  useEffect(() => {
    setSelectedTemplateId('');
    setTemplateSearch('');
    setTemplatesAnchorEl(null);
    setNewText('');
  }, [contactId, channel]);

  useEffect(() => {
    if (projectId) {
      setEffectiveProjectId(projectId);
      return;
    }
    if (!leadId) {
      setEffectiveProjectId(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'leads', leadId))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? (snap.data() as { projectId?: unknown }) : null;
        const nextProjectId = String(data?.projectId || '').trim() || null;
        setEffectiveProjectId(nextProjectId);
      })
      .catch(() => {
        if (!cancelled) setEffectiveProjectId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, projectId]);

  useEffect(() => {
    if (!effectiveProjectId) {
      setProjectMeta(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'projects', effectiveProjectId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setProjectMeta(null);
          return;
        }
        const data = snap.data() as { name?: unknown; logoUrl?: unknown; brochureUrl?: unknown; websiteSlug?: unknown };
        setProjectMeta({
          name: String(data?.name || '').trim() || null,
          logoUrl: String(data?.logoUrl || '').trim() || null,
          brochureUrl: String(data?.brochureUrl || '').trim() || null,
          websiteSlug: String(data?.websiteSlug || '').trim() || null,
        });
      })
      .catch(() => {
        if (!cancelled) setProjectMeta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveProjectId]);

  useEffect(() => {
    if (channel !== 'whatsapp') return;
    const unsub = onSnapshot(
      query(collection(db, 'whatsappQuickReplies'), orderBy('sortOrder', 'asc')),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((row: any) => row.active !== false)
          .filter((row: any) => {
            const scope = String(row.scope || 'global');
            if (scope !== 'project') return true;
            return !!effectiveProjectId && String(row.projectId || '') === String(effectiveProjectId || '');
          });
        setQuickReplies(rows);
      }
    );
    return () => unsub();
  }, [channel, effectiveProjectId]);

  // A new conversation starts from the newest page again.
  useEffect(() => {
    setPageSize(MESSAGE_PAGE);
  }, [contactPhone]);

  useEffect(() => {
    if (!contactPhone) return;
    setLoading(true);
    // Newest first, then shown oldest → newest. Long chats load older
    // messages with "Load earlier messages".
    const q = query(
      collection(db, config.collection),
      where('contactPhone', '==', contactPhone),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );
    const unsub = onSnapshot(q, (snap) => {
      const msgs: ChannelMessage[] = snap.docs.map((d) => ({
        id: d.id,
        channel: (d.data().channel as MessageChannel) || channel,
        ...d.data(),
      } as ChannelMessage)).reverse();
      setMessages(msgs);
      setHasEarlier(snap.size >= pageSize);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [contactPhone, config.collection, channel, pageSize]);

  // Scroll to the bottom only when a newer message arrives, not when
  // earlier messages are loaded above.
  const newestIdRef = useRef<string | null>(null);
  useEffect(() => {
    const newest = messages[messages.length - 1]?.id || null;
    if (newest && newest !== newestIdRef.current) {
      // Scroll only the message list, never the page around the chat (it sits inside cards on the unit page).
      const end = messagesEndRef.current;
      let box = end?.parentElement || null;
      while (box && !/(auto|scroll)/.test(getComputedStyle(box).overflowY)) box = box.parentElement;
      if (box) box.scrollTo({ top: box.scrollHeight, behavior: newestIdRef.current ? 'smooth' : 'auto' });
      else end?.scrollIntoView({ behavior: newestIdRef.current ? 'smooth' : 'auto', block: 'nearest' });
    }
    newestIdRef.current = newest;
  }, [messages]);

  useEffect(() => {
    const handleVisibility = () => {
      setDocumentVisible(document.visibilityState === 'visible');
      setWindowFocused(document.hasFocus());
    };
    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => setWindowFocused(false);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    if (!messages.length || !currentUser?.email) return;
    if (!documentVisible || !windowFocused) return;
    const listEl = listRef.current;
    if (!listEl) return;
    const rect = listEl.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const unread = messages.filter(
      (m) => m.direction === 'inbound' && !m.readAt
    );
    if (!unread.length) return;
    const timeoutId = window.setTimeout(() => {
      const latestRect = listRef.current?.getBoundingClientRect();
      if (!latestRect || latestRect.width <= 0 || latestRect.height <= 0) return;
      if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
      // Small batches: one bad message must not stop the rest being marked.
      for (let i = 0; i < unread.length; i += 100) {
        const batch = writeBatch(db);
        for (const msg of unread.slice(i, i + 100)) {
          batch.update(doc(db, config.collection, msg.id), {
            readAt: serverTimestamp(),
            readBy: currentUser.email,
          });
        }
        batch.commit().catch((err) => console.warn('Could not mark messages read', err));
      }
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [messages, currentUser?.email, config.collection, documentVisible, windowFocused]);

  const lastInboundAt = useMemo(() => {
    const inbound = messages.filter((m) => m.direction === 'inbound');
    if (!inbound.length) return null;
    const last = inbound[inbound.length - 1];
    if (!last.createdAt) return null;
    return typeof last.createdAt.toDate === 'function'
      ? last.createdAt.toDate()
      : new Date(last.createdAt as unknown as number);
  }, [messages]);

  const outsideWindow = useMemo(() => {
    if (channel !== 'whatsapp' || !lastInboundAt) return true;
    return Date.now() - lastInboundAt.getTime() > 24 * 60 * 60 * 1000;
  }, [channel, lastInboundAt]);

  const freeformBlocked =
    channel === 'whatsapp' && outsideWindow;

  useEffect(() => {
    if (!freeformBlocked && selectedTemplateId) {
      setSelectedTemplateId('');
      setTemplateSearch('');
      setTemplatesAnchorEl(null);
      setNewText('');
    }
  }, [freeformBlocked, selectedTemplateId]);

  const selectedTemplate = useMemo(
    () => templates.find((row) => row.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const visibleTemplates = useMemo(() => {
    const search = String(templateSearch || '').trim().toLowerCase();
    return templates.filter((row) => {
      if (!search) return true;
      const label = String(row.label || row.name || row.id || '').toLowerCase();
      const body = String(row.bodyText || '').toLowerCase();
      const language = String(row.language || '').toLowerCase();
      return label.includes(search) || body.includes(search) || language.includes(search);
    });
  }, [templates, templateSearch]);

  const quickReplyCategories = useMemo(() => {
    const values = Array.from(
      new Set(
        quickReplies
          .map((row) => String(row.category || '').trim())
          .filter(Boolean)
      )
    );
    return values.sort((a, b) => a.localeCompare(b));
  }, [quickReplies]);

  const visibleQuickReplies = useMemo(() => {
    const search = String(quickReplySearch || '').trim().toLowerCase();
    return quickReplies.filter((row) => {
      const category = String(row.category || '').trim();
      if (quickReplyCategoryFilter !== 'all' && category !== quickReplyCategoryFilter) {
        return false;
      }
      if (!search) return true;
      const haystack = `${String(row.label || '')} ${String(row.text || '')} ${category}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [quickReplies, quickReplyCategoryFilter, quickReplySearch]);

  const buildTemplatePreviewText = useCallback((template: WhatsappTemplateRow) => {
    const templateVariables = Array.isArray(template.variables) ? template.variables : [];
    const sourceMap: Record<string, string> = {
      customer_name: String(contactName || '').trim() || 'Customer',
      customer_first_name: String(contactName || '').trim() || 'Customer',
      contact_name: String(contactName || '').trim() || 'Customer',
      lead_name: String(contactName || '').trim() || 'Customer',
      project_name: String(projectMeta?.name || '').trim(),
      project_slug: String(projectMeta?.websiteSlug || '').trim(),
      project_brochure_url: String(projectMeta?.brochureUrl || '').trim(),
      phone: String(contactPhone || '').trim(),
      phone_number: String(contactPhone || '').trim(),
    };

    let resolvedText = String(template.bodyText || '').trim();
    if (resolvedText && templateVariables.length) {
      templateVariables.forEach((entry, index) => {
        const sourceKey = String(entry?.source || entry?.key || '').trim();
        const replacement = formatWhatsappVariableValue(
          sourceKey,
          sourceMap[sourceKey] ||
          String(entry?.defaultValue || '').trim() ||
          '-'
        );
        resolvedText = resolvedText.replace(`{{${index + 1}}}`, replacement);
      });
    }
    return resolvedText;
  }, [contactName, contactPhone, projectMeta?.brochureUrl, projectMeta?.name, projectMeta?.websiteSlug]);

  const handleSend = async () => {
    const trimmed = newText.trim();
    if ((!trimmed && !pendingAttachment) || sending || freeformBlocked) return;
    setSending(true);
    setError(null);
    try {
      let uploadedAttachment:
        | {
            mediaType: WhatsappAttachmentKind;
            mediaUrl: string;
            mediaFilename: string;
            mediaMimeType: string;
          }
        | null = null;
      if (pendingAttachment) {
        const file = pendingAttachment.file;
        const safeName = sanitizeWhatsappAttachmentName(file.name);
        const storagePath = [
          'whatsappAttachments',
          currentUser?.uid || 'unknown',
          `${Date.now()}-${safeName}`,
        ].join('/');
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, {
          contentType: file.type || 'application/octet-stream',
        });
        const mediaUrl = await getDownloadURL(storageRef);
        uploadedAttachment = {
          mediaType: pendingAttachment.kind,
          mediaUrl,
          mediaFilename: safeName,
          mediaMimeType: file.type || 'application/octet-stream',
        };
      }
      const sendFn = httpsCallable(functions, config.sendFn);
      await sendFn({
        senderId: selectedSenderId || undefined,
        to: contactPhone,
        text: trimmed || undefined,
        ...(uploadedAttachment || {}),
        leadId: leadId || undefined,
        projectId: projectId || undefined,
      });
      setNewText('');
      setPendingAttachment((prev) => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return null;
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const openAttachmentPicker = (accept: string) => {
    attachmentAcceptRef.current = accept;
    setAttachmentMenuAnchorEl(null);
    requestAnimationFrame(() => {
      if (attachmentInputRef.current) {
        attachmentInputRef.current.accept = accept;
        attachmentInputRef.current.value = '';
        attachmentInputRef.current.click();
      }
    });
  };

  const handleAttachmentSelected = (file: File | null) => {
    if (!file) return;
    const kind = detectWhatsappAttachmentKind(file);
    const limit = WHATSAPP_ATTACHMENT_LIMITS[kind];
    if (file.size > limit) {
      const mb = Math.floor(limit / (1024 * 1024));
      setError(`${kind === 'document' ? 'Document' : kind === 'video' ? 'Video' : 'Image'} must be ${mb} MB or smaller.`);
      return;
    }
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return {
        file,
        kind,
        previewUrl: kind === 'image' || kind === 'video' ? URL.createObjectURL(file) : undefined,
      };
    });
    setSelectedTemplateId('');
  };

  const clearPendingAttachment = () => {
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  };

  const handleSendTemplate = async () => {
    if (channel !== 'whatsapp' || !selectedTemplate || sending) return;
    if (!selectedTemplate) {
      setError('Select a WhatsApp template.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const templateVariables = Array.isArray(selectedTemplate.variables) ?
        selectedTemplate.variables :
        [];
      const buttonVariables = Array.isArray(selectedTemplate.buttonVariables) ?
        selectedTemplate.buttonVariables :
        [];
      const sourceMap: Record<string, string> = {
        customer_name: String(contactName || '').trim() || 'Customer',
        customer_first_name: String(contactName || '').trim() || 'Customer',
        contact_name: String(contactName || '').trim() || 'Customer',
        lead_name: String(contactName || '').trim() || 'Customer',
        project_name: String(projectMeta?.name || '').trim(),
        project_slug: String(projectMeta?.websiteSlug || '').trim(),
        project_brochure_url: String(projectMeta?.brochureUrl || '').trim(),
        phone: String(contactPhone || '').trim(),
        phone_number: String(contactPhone || '').trim(),
      };
      const templateComponents: any[] = [];
      const headerFormat = String(
        selectedTemplate.headerFormat || ''
      ).toUpperCase();
      const headerImageUrlRaw = String(
        selectedTemplate.headerImageUrl || ''
      ).trim();
      const headerDocumentUrlRaw = String(
        selectedTemplate.headerDocumentUrl || ''
      ).trim();
      const headerImageUrl =
        headerImageUrlRaw === WHATSAPP_HEADER_PROJECT_LOGO_TOKEN ||
        headerImageUrlRaw === WHATSAPP_HEADER_VOUCHER_OR_PROJECT_LOGO_TOKEN
          ? String(projectMeta?.logoUrl || '').trim()
          : headerImageUrlRaw;

      if (headerFormat === 'IMAGE') {
        if (!headerImageUrl) {
          throw new Error(
            'Selected template requires a project logo image.'
          );
        }
        templateComponents.push({
          type: 'header',
          parameters: [{ type: 'image', image: { link: headerImageUrl } }],
        });
      } else if (headerFormat === 'DOCUMENT') {
        const documentUrl = headerDocumentUrlRaw === WHATSAPP_HEADER_PROJECT_BROCHURE_TOKEN
          ? String(projectMeta?.brochureUrl || '').trim()
          : headerDocumentUrlRaw;
        if (!documentUrl) {
          throw new Error(
            'Selected template requires a project brochure document URL.'
          );
        }
        templateComponents.push({
          type: 'header',
          parameters: [{
            type: 'document',
            document: { link: documentUrl, filename: 'Project Brochure.pdf' },
          }],
        });
      } else if (headerFormat === 'LOCATION') {
        const lat = parseFloat(String(selectedTemplate.headerLatitude || ''));
        const lng = parseFloat(String(selectedTemplate.headerLongitude || ''));
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          throw new Error(
            'Selected template has invalid location coordinates.'
          );
        }
        templateComponents.push({
          type: 'header',
          parameters: [{
            type: 'location',
            location: {
              latitude: lat,
              longitude: lng,
              name: String(
                selectedTemplate.headerLocationName || ''
              ).trim() || undefined,
              address: String(
                selectedTemplate.headerLocationAddress || ''
              ).trim() || undefined,
            },
          }],
        });
      }

      if (buttonVariables.length) {
        buttonVariables.forEach((entry) => {
          const sourceKey = String(entry?.source || entry?.key || '').trim();
          const text = formatWhatsappVariableValue(
            sourceKey,
            sourceMap[sourceKey] ||
              String(entry?.defaultValue || '').trim() ||
              '-'
          );
          templateComponents.push({
            type: 'button',
            sub_type: 'url',
            index: String(entry?.index ?? '0'),
            parameters: [{
              type: 'text',
              text,
            }],
          });
        });
      }

      if (templateVariables.length) {
        templateComponents.push({
          type: 'body',
          parameters: templateVariables.map((entry) => {
            const sourceKey = String(entry?.source || entry?.key || '').trim();
            const text = formatWhatsappVariableValue(
              sourceKey,
              sourceMap[sourceKey] ||
                String(entry?.defaultValue || '').trim() ||
                '-'
            );
            return {
              type: 'text',
              text,
            };
          }),
        });
      }

      const resolvedText = buildTemplatePreviewText(selectedTemplate);

      const sendFn = httpsCallable(functions, config.sendFn);
      await sendFn({
        senderId: selectedSenderId || undefined,
        to: contactPhone,
        leadId: leadId || undefined,
        projectId: projectId || undefined,
        templateName: String(selectedTemplate.name || '').trim(),
        templateLanguage:
          String(selectedTemplate.language || 'en').trim() || 'en',
        templateComponents,
        resolvedText: resolvedText || undefined,
      });
      setSelectedTemplateId('');
    } catch (e: any) {
      setError(e?.message || 'Failed to send template');
    } finally {
      setSending(false);
    }
  };

  const handleInsertQuickReply = (reply: WhatsappQuickReplyRow) => {
    if (freeformBlocked) {
      setError('Quick replies work only inside the 24-hour window. Use template.');
      return;
    }
    const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
    const resolvedText = String(reply.text || '').trim().replace(/\[Your Name\]/g, userName);
    setNewText(resolvedText);
    setQuickRepliesAnchorEl(null);
  };

  const handleSelectTemplate = (template: WhatsappTemplateRow) => {
    setSelectedTemplateId((prev) => {
      const next = prev === template.id ? '' : template.id;
      setNewText(next ? buildTemplatePreviewText(template) : '');
      return next;
    });
    setTemplateSearch('');
    setTemplatesAnchorEl(null);
  };

  const handleSendQuickReply = async (reply: WhatsappQuickReplyRow) => {
    const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
    const text = String(reply.text || '').trim().replace(/\[Your Name\]/g, userName);
    if (!text || sending || freeformBlocked) {
      if (freeformBlocked) {
        setError('Quick replies work only inside the 24-hour window. Use template.');
      }
      return;
    }
    setSending(true);
    setError(null);
    try {
      const sendFn = httpsCallable(functions, config.sendFn);
      await sendFn({
        senderId: selectedSenderId || undefined,
        to: contactPhone,
        text,
        leadId: leadId || undefined,
        projectId: effectiveProjectId || undefined,
        metadata: {
          quickReplyId: reply.id,
          quickReplyLabel: String(reply.label || '').trim() || null,
          quickReplyCategory: String(reply.category || '').trim() || null,
        },
      });
      setQuickRepliesAnchorEl(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to send quick reply');
    } finally {
      setSending(false);
    }
  };

  const getFailureMessage = (msg: ChannelMessage) => {
    const providerError = msg.providerResponse?.error as
      | { message?: unknown; error_data?: { details?: unknown } }
      | undefined;
    const providerMessage = String(providerError?.message || '').trim();
    const providerDetails = String(providerError?.error_data?.details || '').trim();
    const directError = String((msg as any).error || '').trim();
    return (
      providerDetails ||
      providerMessage ||
      directError ||
      'Message failed to send.'
    );
  };

  const handleResendMessage = async (msg: ChannelMessage) => {
    if (resendingMessageId || sending) return;
    setResendingMessageId(msg.id);
    setError(null);
    try {
      const sendFn = httpsCallable(functions, config.sendFn);
      const payload: Record<string, unknown> = {
        senderId: String(msg.senderId || selectedSenderId || '').trim() || undefined,
        to: contactPhone,
        leadId: leadId || undefined,
        projectId: projectId || undefined,
      };

      if (msg.type === 'template') {
        payload.templateName = String(msg.templateName || '').trim() || undefined;
        payload.templateLanguage = String((msg as any).templateLanguage || 'en').trim() || 'en';
        payload.templateComponents = Array.isArray(msg.templateComponents) ? msg.templateComponents : [];
        payload.resolvedText = getMessageDisplayText(msg, templateEdits) || undefined;
        payload.templateButtons = getMessageTemplateButtons(msg, templateEdits);
      } else if (['image', 'video', 'document'].includes(String(msg.type || ''))) {
        const mediaInfo = (msg.mediaInfo || {}) as Record<string, unknown>;
        payload.text = String(msg.text || '').trim() || undefined;
        payload.mediaType = msg.type;
        payload.mediaUrl = String(mediaInfo.downloadUrl || '').trim();
        payload.mediaFilename = String(mediaInfo.filename || '').trim() || undefined;
        payload.mediaMimeType = String(mediaInfo.mimeType || '').trim() || undefined;
        if (!payload.mediaUrl) {
          throw new Error('Cannot resend media message because the attachment URL is missing.');
        }
      } else {
        payload.text = String(msg.text || '').trim();
      }

      await sendFn(payload);
    } catch (e: any) {
      setError(e?.message || 'Failed to resend message');
    } finally {
      setResendingMessageId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const statusIcon = (status: string) => {
    if (status === 'read')
      return <DoneAllIcon sx={{ fontSize: 14, color: '#53bdeb' }} />;
    if (status === 'delivered')
      return <DoneAllIcon sx={{ fontSize: 14, color: MUTED }} />;
    if (status === 'sent')
      return <DoneIcon sx={{ fontSize: 14, color: MUTED }} />;
    if (status === 'failed')
      return <ErrorOutlineOutlinedIcon sx={{ fontSize: 14, color: '#d32f2f' }} />;
    return null;
  };

  useEffect(() => {
    if (channel !== 'whatsapp') return;
    const pending = messages.filter((msg) => {
      if (msg.type !== 'image') return false;
      const mediaId = String(msg.mediaInfo?.mediaId || '').trim();
      return !!mediaId && !mediaPreviewUrls[msg.id];
    });
    if (!pending.length) return;

    let cancelled = false;
    const callable = httpsCallable(functions, 'whatsappGetMediaPreview');

    Promise.all(
      pending.map(async (msg) => {
        const mediaId = String(msg.mediaInfo?.mediaId || '').trim();
        if (!mediaId) return null;
        try {
          const result = await callable({
            mediaId,
            senderId: msg.senderId || undefined,
            senderPhoneNumberId: msg.senderPhoneNumberId || undefined,
            mimeType: String(msg.mediaInfo?.mimeType || '').trim() || undefined,
          });
          const data = result.data as { dataUrl?: string };
          return data?.dataUrl ? { id: msg.id, dataUrl: data.dataUrl } : null;
        } catch (err) {
          console.warn('Failed to load WhatsApp media preview', err);
          return null;
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      const nextRows = rows.filter(
        (row): row is { id: string; dataUrl: string } => !!row?.id && !!row?.dataUrl
      );
      if (!nextRows.length) return;
      setMediaPreviewUrls((prev) => {
        const next = { ...prev };
        nextRows.forEach((row) => {
          next[row.id] = row.dataUrl;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [channel, mediaPreviewUrls, messages]);

  useEffect(() => {
    return () => {
      if (pendingAttachment?.previewUrl) {
        URL.revokeObjectURL(pendingAttachment.previewUrl);
      }
    };
  }, [pendingAttachment?.previewUrl]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height,
        fontFamily: FONT_BODY,
        overflow: 'hidden',
      }}
    >
      {showHeader && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}` }}
        >
          {onBack && (
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <WhatsAppIcon sx={{ color: config.color, fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {editingName ? (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <TextField
                  size="small"
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                  placeholder="Enter name"
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, height: 30 } }}
                />
                <IconButton size="small" onClick={handleSaveName} sx={{ color: '#25d366' }}>
                  <CheckIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton size="small" onClick={() => setEditingName(false)} sx={{ color: MUTED }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3 }}>
                  {contactName || '--'}
                </Typography>
                {(hasLead || hasBroker || hasCustomer) && (
                  <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ minWidth: 0 }}>
                    {hasLead && (
                      <Chip
                        size="small"
                        label="Lead"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: 'rgba(157,111,50,0.16)',
                          color: GOLD,
                          '& .MuiChip-label': { px: 0.8 },
                        }}
                      />
                    )}
                    {hasBroker && (
                      <Chip
                        size="small"
                        label="Broker"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: 'rgba(25,118,210,0.14)',
                          color: '#1565c0',
                          '& .MuiChip-label': { px: 0.8 },
                        }}
                      />
                    )}
                    {hasCustomer && (
                      <Tooltip title={customerTooltip}>
                        <Chip
                          size="small"
                          label="Customer"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: 'rgba(14,165,233,0.14)',
                            color: '#0284C7',
                            '& .MuiChip-label': { px: 0.8 },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                )}
                <IconButton
                  size="small"
                  onClick={() => { setEditNameValue(contactName || ''); setEditingName(true); }}
                  sx={{ p: 0.25, color: MUTED, '&:hover': { color: GOLD } }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            )}
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              {formatPhoneDisplay(contactPhone)}
            </Typography>
          </Box>
          {!hasLead && !hasCustomer && onAddLead && (
            <Tooltip title="Add as new lead">
              <IconButton
                size="small"
                onClick={() => onAddLead(contactPhone, contactName || '')}
                sx={{ color: GOLD }}
              >
                <PersonAddAltOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          {leadId && onOpenLead && (
            <Tooltip title="Open Lead">
              <IconButton
                size="small"
                onClick={() => onOpenLead(leadId)}
                sx={{ color: GOLD }}
              >
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {customerUnitId && canOpenCustomerUnit && onOpenCustomerUnit && (
            <Tooltip title={`Open Unit ${customerUnitNumber || customerUnitId}${customerProjectName ? ` · ${customerProjectName}` : ''}`}>
              <IconButton
                size="small"
                onClick={() => onOpenCustomerUnit(customerUnitId)}
                sx={{ color: '#0284C7' }}
              >
                <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {brokerId && onOpenBroker && (
            <Tooltip title="Open Broker">
              <IconButton
                size="small"
                onClick={() => onOpenBroker(brokerId)}
                sx={{ color: '#1565c0' }}
              >
                <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}

      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          bgcolor: '#fafaf9',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={28} sx={{ color: GOLD }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 4, color: MUTED }}>
            <Typography sx={{ fontSize: 14 }}>No messages yet</Typography>
          </Box>
        ) : (
          <>
          {hasEarlier && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 0.5 }}>
              <Box component="button" type="button" onClick={() => setPageSize((n) => n + MESSAGE_PAGE)}
                sx={{ border: '1px solid #e0dbd2', bgcolor: '#fff', borderRadius: 999, px: 1.75, py: 0.6, fontSize: 12.5, fontWeight: 600, color: GOLD, cursor: 'pointer' }}>
                Load earlier messages
              </Box>
            </Box>
          )}
          {messages.map((msg, index) => {
            const messageDate = formatChatDate(msg.createdAt ?? msg.sentAt ?? msg.receivedAt);
            const previous = messages[index - 1];
            const previousDate = previous
              ? formatChatDate(previous.createdAt ?? previous.sentAt ?? previous.receivedAt)
              : '';
            const interactiveParts = interactiveMessageParts(msg);
            const messageButtons = msg.type === 'interactive'
              ? interactiveParts.buttons : getMessageTemplateButtons(msg, templateEdits);
            return (
            <React.Fragment key={msg.id}>
            {messageDate && messageDate !== previousDate && (
              <Box sx={{ alignSelf: 'center', bgcolor: '#eae7e2', color: MUTED, borderRadius: 1.5, px: 1.25, py: 0.4, my: 0.75, fontSize: 11.5, fontWeight: 600 }}>
                {messageDate}
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '78%',
                alignSelf: msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: '10px',
                  borderTopLeftRadius: msg.direction === 'inbound' ? '2px' : '10px',
                  borderTopRightRadius: msg.direction === 'outbound' ? '2px' : '10px',
                  bgcolor:
                    msg.direction === 'outbound'
                      ? config.outboundBubbleColor
                      : '#ffffff',
                  border:
                    msg.direction === 'inbound'
                      ? `0.5px solid ${BORDER}`
                      : 'none',
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                {msg.templateName && (
                  <Tooltip title={`Meta template: ${msg.templateName}`} placement="top" arrow>
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.25, width: 'fit-content' }}>
                      <DescriptionOutlinedIcon sx={{ fontSize: 12, color: MUTED }} />
                      <Typography sx={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }}>
                        {getTemplateDisplayName(msg.templateName)}
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
                {interactiveParts.document && (
                  <Box component={interactiveParts.document.url ? 'a' : 'div'}
                    href={interactiveParts.document.url || undefined}
                    target={interactiveParts.document.url ? '_blank' : undefined}
                    rel={interactiveParts.document.url ? 'noopener noreferrer' : undefined}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 0.75,
                      border: '1px solid #b8dba7', borderRadius: 1.5, bgcolor: '#f7fcf4',
                      color: TEXT, textDecoration: 'none',
                      '&:hover': interactiveParts.document.url ? { bgcolor: '#eff9e9' } : undefined }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 24, color: '#357a38', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, overflowWrap: 'anywhere' }}>
                        {interactiveParts.document.filename}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: MUTED }}>
                        PDF attached to WhatsApp message
                      </Typography>
                    </Box>
                    {interactiveParts.document.url && <OpenInNewIcon sx={{ fontSize: 17, color: '#357a38' }} />}
                  </Box>
                )}
                {(() => {
                  const mi = (msg as any).mediaInfo;
                  const payload = (msg as any).payload;
                  if (msg.type === 'image') {
                    const imageUrl =
                      mediaPreviewUrls[msg.id] ||
                      String(mi?.downloadUrl || payload?.image?.link || '').trim();
                    return (
                      <>
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt="WhatsApp media"
                            sx={{
                              display: 'block',
                              width: '100%',
                              maxWidth: 280,
                              maxHeight: 280,
                              objectFit: 'cover',
                              borderRadius: '8px',
                              mb: msg.text ? 0.5 : 0,
                              border: `0.5px solid ${BORDER}`,
                            }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MUTED, mb: 0.5 }}>
                            <span style={{ fontSize: 16 }}>📷</span>
                            <Typography sx={{ fontSize: 12, fontStyle: 'italic' }}>Photo</Typography>
                          </Box>
                        )}
                        {msg.text && <Typography sx={{ fontSize: 13, lineHeight: 1.45, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{renderWhatsAppFormattedText(msg.text)}</Typography>}
                      </>
                    );
                  }
                  if (msg.type === 'video') {
                    const videoUrl = String(mi?.downloadUrl || payload?.video?.link || '').trim();
                    return (
                      <>
                        {videoUrl ? (
                          <Box
                            component="video"
                            src={videoUrl}
                            controls
                            sx={{
                              display: 'block',
                              width: '100%',
                              maxWidth: 280,
                              maxHeight: 280,
                              borderRadius: '8px',
                              mb: msg.text ? 0.5 : 0,
                              border: `0.5px solid ${BORDER}`,
                              bgcolor: '#000',
                            }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MUTED, mb: 0.5 }}>
                            <span style={{ fontSize: 16 }}>🎥</span>
                            <Typography sx={{ fontSize: 12, fontStyle: 'italic' }}>Video</Typography>
                          </Box>
                        )}
                        {msg.text && <Typography sx={{ fontSize: 13, lineHeight: 1.45, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{renderWhatsAppFormattedText(msg.text)}</Typography>}
                      </>
                    );
                  }
                  if (msg.type === 'document') {
                    const fname = mi?.filename || 'Document';
                    const docUrl = String(mi?.downloadUrl || payload?.document?.link || '').trim();
                    return (
                      <Box
                        component={docUrl ? 'a' : 'div'}
                        href={docUrl || undefined}
                        target={docUrl ? '_blank' : undefined}
                        rel={docUrl ? 'noopener noreferrer' : undefined}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          color: docUrl ? '#1a73e8' : TEXT,
                          textDecoration: docUrl ? 'none' : undefined,
                        }}
                      >
                        <span style={{ fontSize: 16 }}>📄</span>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, wordBreak: 'break-word', fontWeight: 600 }}>{fname}</Typography>
                          {msg.text ? (
                            <Typography sx={{ fontSize: 12, color: MUTED, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {renderWhatsAppFormattedText(msg.text)}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  }
                  if (msg.type === 'audio') {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MUTED }}>
                        <span style={{ fontSize: 16 }}>🎵</span>
                        <Typography sx={{ fontSize: 12, fontStyle: 'italic' }}>Audio message</Typography>
                      </Box>
                    );
                  }
                  if (msg.type === 'location') {
                    const lat = mi?.latitude || payload?.location?.latitude;
                    const lng = mi?.longitude || payload?.location?.longitude;
                    const locName = mi?.name || payload?.location?.name || '';
                    const locAddr = mi?.address || payload?.location?.address || '';
                    const label = [locName, locAddr].filter(Boolean).join(', ') || msg.text;
                    return (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <span style={{ fontSize: 16 }}>📍</span>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Location</Typography>
                        </Box>
                        {label && <Typography sx={{ fontSize: 12, color: MUTED, wordBreak: 'break-word', mb: 0.5 }}>{label}</Typography>}
                        {lat && lng && (
                          <Typography
                            component="a"
                            href={`https://www.google.com/maps?q=${lat},${lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: 12, color: '#1a73e8', textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            Open in Maps
                          </Typography>
                        )}
                      </>
                    );
                  }
                  if (msg.type === 'sticker') {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MUTED }}>
                        <span style={{ fontSize: 20 }}>🏷️</span>
                        <Typography sx={{ fontSize: 12, fontStyle: 'italic' }}>Sticker</Typography>
                      </Box>
                    );
                  }
                  if (msg.type === 'contacts') {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span style={{ fontSize: 16 }}>👤</span>
                        <Typography sx={{ fontSize: 13, wordBreak: 'break-word' }}>{msg.text || 'Contact card'}</Typography>
                      </Box>
                    );
                  }
                  if (msg.type === 'template') {
                    const bodyText = getMessageDisplayText(msg, templateEdits);
                    return (
                      <>
                        {bodyText && (
                          <Typography sx={{ fontSize: 13, lineHeight: 1.45, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {renderWhatsAppFormattedText(bodyText)}
                          </Typography>
                        )}
                      </>
                    );
                  }
                  if (msg.type === 'unsupported') {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: MUTED }}>
                        <ErrorOutlineOutlinedIcon sx={{ fontSize: 16, mt: '2px' }} />
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                            Unsupported WhatsApp message
                          </Typography>
                          <Typography sx={{ fontSize: 12, lineHeight: 1.4 }}>
                            {formatUnsupportedMessageLabel(msg).replace(/^Unsupported WhatsApp message\s*/, '') || 'Meta did not provide readable content for this message type.'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }
                  return (
                    <Typography sx={{ fontSize: 13, lineHeight: 1.45, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {renderWhatsAppFormattedText(msg.text || '')}
                    </Typography>
                  );
                })()}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={0.5}
                  sx={{ mt: 0.25 }}
                >
                  <Typography sx={{ fontSize: 10, color: MUTED }}>
                    {formatBubbleTime(msg.createdAt ?? msg.sentAt ?? msg.receivedAt)}
                  </Typography>
                  {msg.direction === 'outbound' && msg.status === 'failed' && (
                    <>
                      <Tooltip title={getFailureMessage(msg)}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: '#d32f2f',
                          }}
                        >
                          {statusIcon(msg.status)}
                        </Box>
                      </Tooltip>
                      <Tooltip title="Resend">
                        <Box component="span">
                          <IconButton
                            size="small"
                            onClick={() => handleResendMessage(msg)}
                            disabled={resendingMessageId === msg.id}
                            sx={{
                              p: 0.25,
                              color: GOLD,
                              '&.Mui-disabled': { color: MUTED },
                            }}
                          >
                            {resendingMessageId === msg.id ? (
                              <CircularProgress size={12} sx={{ color: GOLD }} />
                            ) : (
                              <ReplayIcon sx={{ fontSize: 13 }} />
                            )}
                          </IconButton>
                        </Box>
                      </Tooltip>
                    </>
                  )}
                  {msg.direction === 'outbound' && msg.status !== 'failed' && statusIcon(msg.status)}
                </Stack>
              </Box>
              {messageButtons.length > 0 && (
                <Stack gap={0.5} sx={{ mt: 0.5 }} aria-label="Options sent with this message">
                  {messageButtons.map((label, buttonIndex) => (
                    <Box
                      key={`${buttonIndex}-${label}`}
                      sx={{
                        bgcolor: '#fff',
                        border: `1px solid ${BORDER}`,
                        borderRadius: 1.5,
                        color: '#0878ad',
                        fontSize: 12.5,
                        fontWeight: 600,
                        textAlign: 'center',
                        px: 1.5,
                        py: 0.65,
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
            </React.Fragment>
            );
          })}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ px: 2, pt: 1, pb: 1.25, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
        <input
          ref={attachmentInputRef}
          type="file"
          hidden
          accept={attachmentAcceptRef.current}
          onChange={(event) => {
            handleAttachmentSelected(event.target.files?.[0] || null);
          }}
        />
        {pendingAttachment ? (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              mb: 0.75,
              p: 0.75,
              border: `1px solid ${BORDER}`,
              borderRadius: '8px',
              bgcolor: '#fafafa',
            }}
          >
            {pendingAttachment.kind === 'image' && pendingAttachment.previewUrl ? (
              <Box
                component="img"
                src={pendingAttachment.previewUrl}
                alt={pendingAttachment.file.name}
                sx={{
                  width: 42,
                  height: 42,
                  objectFit: 'cover',
                  borderRadius: '6px',
                  border: `0.5px solid ${BORDER}`,
                }}
              />
            ) : pendingAttachment.kind === 'video' ? (
              <VideocamOutlinedIcon sx={{ color: MUTED, fontSize: 24 }} />
            ) : (
              <InsertDriveFileOutlinedIcon sx={{ color: MUTED, fontSize: 24 }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, wordBreak: 'break-word' }}>
                {pendingAttachment.file.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: MUTED }}>
                {pendingAttachment.kind === 'image' ? 'Image' : pendingAttachment.kind === 'video' ? 'Video' : 'Document'} - {(pendingAttachment.file.size / (1024 * 1024)).toFixed(2)} MB
              </Typography>
            </Box>
            <IconButton size="small" onClick={clearPendingAttachment} disabled={sending}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        ) : null}
        {/* Input row — full width */}
        <Stack direction="row" alignItems="flex-end" gap={1}>
          {senders.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={selectedSenderId}
                onChange={(e) => setSelectedSenderId(e.target.value)}
                sx={{ fontSize: 12, height: 36 }}
              >
                {senders.map((s: any) => (
                  <MenuItem key={s.id} value={s.id} sx={{ fontSize: 12 }}>
                    {s.name || s.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {channel === 'whatsapp' && !selectedTemplate ? (
            <Tooltip
              title={
                freeformBlocked
                  ? 'Attachments work only inside the 24-hour WhatsApp window.'
                  : 'Attach file'
              }
            >
              <Box component="span">
                <IconButton
                  onClick={(e) => setAttachmentMenuAnchorEl(e.currentTarget)}
                  disabled={sending || freeformBlocked}
                  sx={{
                    width: 36,
                    height: 36,
                    color: MUTED,
                    border: `1px solid ${BORDER}`,
                    borderRadius: '8px',
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Tooltip>
          ) : null}
          <TextField
            multiline
            maxRows={3}
            placeholder={
              selectedTemplate
                ? `Template selected: ${String(
                    selectedTemplate.label || selectedTemplate.name || ''
                  )}`
                : 'Type a message...'
            }
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending || freeformBlocked}
            InputProps={{
              readOnly: !!selectedTemplate,
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: 13,
                fontFamily: FONT_BODY,
                borderRadius: '8px',
              },
              '& .MuiInputBase-input': {
                color: selectedTemplate ? `${TEXT} !important` : undefined,
                WebkitTextFillColor: selectedTemplate ? `${TEXT} !important` : undefined,
                opacity: 1,
                caretColor: selectedTemplate ? TEXT : undefined,
              },
            }}
            size="small"
          />
          <IconButton
            onClick={selectedTemplate ? handleSendTemplate : handleSend}
            disabled={
              selectedTemplate ?
                sending :
                (!newText.trim() && !pendingAttachment) || sending || freeformBlocked
            }
            sx={{
              bgcolor: GOLD,
              color: '#fff',
              width: 36,
              height: 36,
              '&:hover': { bgcolor: '#8a6129' },
              '&.Mui-disabled': { bgcolor: BORDER, color: MUTED },
            }}
          >
            {sending ? (
              <CircularProgress size={16} sx={{ color: '#fff' }} />
            ) : (
              <SendIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Stack>
        <Menu
          anchorEl={attachmentMenuAnchorEl}
          open={Boolean(attachmentMenuAnchorEl)}
          onClose={() => setAttachmentMenuAnchorEl(null)}
        >
          <MenuItem onClick={() => openAttachmentPicker('image/*')}>
            <ImageOutlinedIcon sx={{ fontSize: 18, mr: 1, color: MUTED }} />
            Image
          </MenuItem>
          <MenuItem onClick={() => openAttachmentPicker('video/*')}>
            <VideocamOutlinedIcon sx={{ fontSize: 18, mr: 1, color: MUTED }} />
            Video
          </MenuItem>
          <MenuItem onClick={() => openAttachmentPicker('.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain')}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, mr: 1, color: MUTED }} />
            Document
          </MenuItem>
        </Menu>

        {/* Action row — Quick Replies + Template picker */}
        {channel === 'whatsapp' && (
          <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.75 }}>
            {!freeformBlocked ? (
              <Tooltip title="Quick replies">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => setQuickRepliesAnchorEl(e.currentTarget)}
                  sx={{
                    minWidth: 'auto',
                    px: 1.1,
                    py: 0.3,
                    borderColor: BORDER,
                    color: MUTED,
                    textTransform: 'none',
                    fontSize: 12,
                  }}
                >
                  Quick Replies
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Choose a template">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => setTemplatesAnchorEl(e.currentTarget)}
                  sx={{
                    minWidth: 'auto',
                    px: 1.1,
                    py: 0.3,
                    borderColor: BORDER,
                    color: MUTED,
                    textTransform: 'none',
                    fontSize: 12,
                  }}
                >
                  Templates
                </Button>
              </Tooltip>
            )}
            {onSendBrochure && (
              <Button size="small" variant="outlined" onClick={(event) => onSendBrochure(event.currentTarget, lastInboundAt)}
                sx={{ minWidth: 'auto', px: 1.1, py: 0.3, borderColor: BORDER,
                  color: MUTED, textTransform: 'none', fontSize: 12 }}>
                Send brochure PDF
              </Button>
            )}
          </Stack>
        )}
      </Box>
      <Popover
        open={Boolean(quickRepliesAnchorEl)}
        anchorEl={quickRepliesAnchorEl}
        onClose={() => setQuickRepliesAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 360,
            maxWidth: 'calc(100vw - 24px)',
            p: 1,
            borderRadius: 2,
          },
        }}
      >
        <Stack spacing={1}>
          <TextField
            size="small"
            placeholder="Search quick replies..."
            value={quickReplySearch}
            onChange={(e) => setQuickReplySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 16, color: MUTED }} />
                </InputAdornment>
              ),
            }}
          />
          {quickReplyCategories.length > 0 && (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Chip
                label="All"
                size="small"
                clickable
                color={quickReplyCategoryFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setQuickReplyCategoryFilter('all')}
              />
              {quickReplyCategories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  size="small"
                  clickable
                  color={quickReplyCategoryFilter === category ? 'primary' : 'default'}
                  onClick={() => setQuickReplyCategoryFilter(category)}
                />
              ))}
            </Stack>
          )}
          {freeformBlocked ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Quick replies are hidden because the 24-hour window has expired. Use a template instead.
            </Alert>
          ) : null}
          <List sx={{ py: 0, maxHeight: 320, overflowY: 'auto' }}>
            {visibleQuickReplies.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: MUTED, px: 1, py: 2 }}>
                No quick replies found.
              </Typography>
            ) : (
              visibleQuickReplies.map((reply) => (
                <ListItemButton
                  key={reply.id}
                  onClick={() => handleInsertQuickReply(reply)}
                  sx={{
                    borderRadius: 1.5,
                    alignItems: 'flex-start',
                    px: 1,
                    py: 1,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.25 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                        {String(reply.label || 'Quick Reply')}
                      </Typography>
                      {reply.category ? (
                        <Chip
                          label={String(reply.category)}
                          size="small"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      ) : null}
                    </Stack>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: MUTED,
                        lineHeight: 1.45,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {String(reply.text || '')}
                    </Typography>
                  </Box>
                  {!freeformBlocked ? (
                    <Tooltip title="Send now">
                      <Box component="span">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendQuickReply(reply);
                          }}
                          sx={{ ml: 0.75, color: GOLD }}
                        >
                          <SendIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  ) : null}
                </ListItemButton>
              ))
            )}
          </List>
        </Stack>
      </Popover>

      <Popover
        open={Boolean(templatesAnchorEl)}
        anchorEl={templatesAnchorEl}
        onClose={() => setTemplatesAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 360,
            maxWidth: 'calc(100vw - 24px)',
            p: 1,
            borderRadius: 2,
          },
        }}
      >
        <Stack spacing={1}>
          <TextField
            size="small"
            placeholder="Search templates..."
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 16, color: MUTED }} />
                </InputAdornment>
              ),
            }}
          />
          {templatesLoading ? (
            <Typography sx={{ fontSize: 12, color: MUTED, px: 1, py: 1 }}>
              Loading templates...
            </Typography>
          ) : visibleTemplates.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: MUTED, px: 1, py: 1 }}>
              No templates found.
            </Typography>
          ) : (
            <List sx={{ py: 0, maxHeight: 320, overflowY: 'auto' }}>
              {visibleTemplates.map((template) => {
                const active = selectedTemplateId === template.id;
                return (
                  <ListItemButton
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    sx={{
                      borderRadius: 1.5,
                      alignItems: 'flex-start',
                      px: 1,
                      py: 1,
                      mb: 0.5,
                      bgcolor: active ? 'rgba(157,111,50,0.08)' : 'transparent',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.25 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                          {String(template.label || template.name || template.id)}
                        </Typography>
                        {active ? (
                          <Chip
                            label="Selected"
                            size="small"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        ) : null}
                        {template.language ? (
                          <Chip
                            label={String(template.language)}
                            size="small"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        ) : null}
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: MUTED,
                          lineHeight: 1.45,
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {String(template.bodyText || '') || 'No preview available.'}
                      </Typography>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Stack>
      </Popover>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: 13 }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
